/**
 * Balance de Situación.
 *
 * Selector de fecha "a" + opción de comparar con periodo anterior. Tabla con
 * tres grupos (Activo / Pasivo / Patrimonio Neto) mostrando saldos acumulados.
 * Alerta si activo ≠ pasivo + PN (diff > 1 céntimo).
 *
 * ADMIN-only (la API valida el rol).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, AlertTriangle, Calculator, CheckCircle2, Loader2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { formatCents } from '../lib/money'
import type { BalanceReport, BalanceSection } from '@toga/shared'

/** Hoy en formato YYYY-MM-DD. */
function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Resta un año a YYYY-MM-DD. */
function shiftYear(s: string, delta: number): string {
  const [y, m, d] = s.split('-').map(Number)
  if (!y || !m || !d) return s
  return `${y + delta}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

const ACTIVE_GROUP_ORDER: BalanceSection['key'][] = [
  'ACTIVO_NO_CORRIENTE',
  'ACTIVO_CORRIENTE',
]
const PASSIVE_GROUP_ORDER: BalanceSection['key'][] = [
  'PASIVO_NO_CORRIENTE',
  'PASIVO_CORRIENTE',
  'PATRIMONIO_NETO',
]

export default function AccountingBalance() {
  const { t } = useTranslation('accounting')
  const [at, setAt] = useState<string>(todayStr())
  const [compare, setCompare] = useState<boolean>(true)
  const [report, setReport] = useState<BalanceReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const previousAt = useMemo(() => (compare ? shiftYear(at, -1) : null), [at, compare])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ at })
      if (previousAt) params.set('previousAt', previousAt)
      const data = await api.get<BalanceReport>(
        `/accounting/reports/balance?${params.toString()}`
      )
      setReport(data)
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('balance.errorLoad')
      setError(text)
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [at, previousAt, t])

  useEffect(() => {
    void load()
  }, [load])

  const activeSections = useMemo(
    () =>
      report
        ? ACTIVE_GROUP_ORDER.map((k) => report.sections.find((s) => s.key === k)).filter(
            (s): s is BalanceSection => Boolean(s)
          )
        : [],
    [report]
  )
  const passiveSections = useMemo(
    () =>
      report
        ? PASSIVE_GROUP_ORDER.map((k) => report.sections.find((s) => s.key === k)).filter(
            (s): s is BalanceSection => Boolean(s)
          )
        : [],
    [report]
  )

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-toga-500/15 text-toga-700 dark:text-toga-300">
          <Calculator size={20} />
        </div>
        <div>
          <h1 className="text-xl font-semibold">{t('balance.title')}</h1>
          <p className="text-sm t-muted">
            {t('balance.subtitle')}
          </p>
        </div>
      </header>

      {/* Controles */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
        <div>
          <label className="block text-xs font-medium t-muted mb-1">{t('balance.atDate')}</label>
          <input
            type="date"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
          />
        </div>
        <label className="flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
            className="accent-toga-500"
          />
          {t('balance.compareWithPreviousYear')} {previousAt && <span className="t-muted">({previousAt})</span>}
        </label>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm t-muted">
          <Loader2 size={16} className="animate-spin" />
          {t('balance.loading')}
        </div>
      )}

      {report && !loading && (
        <>
          {/* Alerta de cuadre */}
          {report.balanced ? (
            <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 size={16} />
              {t('balance.balanced')}
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle size={16} />
              {t('balance.notBalanced', { diff: formatCents(report.difference) })}
            </div>
          )}

          {/* Si hay cuentas fuera de todo bloque, se NOMBRAN: el descuadre deja
              de ser un misterio y pasa a ser una lista accionable. */}
          {(report.unclassifiedAccounts?.length ?? 0) > 0 && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle size={16} />
                {t('balance.unclassified')}
              </div>
              <p className="text-xs mt-1 font-mono">
                {report.unclassifiedAccounts.join(' · ')}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BalanceTable
              title={t('balance.assets')}
              sections={activeSections}
              total={report.totalActivo}
              showPrevious={Boolean(previousAt)}
            />
            <BalanceTable
              title={t('balance.liabilitiesAndEquity')}
              sections={passiveSections}
              total={report.totalPasivoPatrimonio}
              showPrevious={Boolean(previousAt)}
            />
          </div>
        </>
      )}
    </div>
  )
}

function BalanceTable({
  title,
  sections,
  total,
  showPrevious,
}: {
  title: string
  sections: BalanceSection[]
  total: number
  showPrevious: boolean
}) {
  const { t } = useTranslation('accounting')
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--border)]">
      <div className="px-4 py-3 bg-[var(--bg-input-soft)] border-b border-[var(--border)]">
        <h2 className="font-semibold t-primary">{title}</h2>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg-input-soft)] t-muted">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{t('balance.concept')}</th>
            <th className="text-right px-4 py-2 font-medium">{t('balance.amount')}</th>
            {showPrevious && (
              <th className="text-right px-4 py-2 font-medium">{t('balance.previousPeriod')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {sections.map((sec) => (
            <SectionBlock key={sec.key} section={sec} showPrevious={showPrevious} />
          ))}
        </tbody>
        <tfoot className="bg-[var(--bg-hover)] border-t border-[var(--border)]">
          <tr>
            <td className="px-4 py-2 font-semibold t-primary">{t('balance.total', { name: title.toLowerCase() })}</td>
            <td className="px-4 py-2 text-right font-mono font-semibold t-primary">{formatCents(total)}</td>
            {showPrevious && <td className="px-4 py-2 text-right font-mono t-muted">—</td>}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function SectionBlock({
  section,
  showPrevious,
}: {
  section: BalanceSection
  showPrevious: boolean
}) {
  return (
    <>
      <tr className="bg-[var(--bg-input-soft)]/60 border-t border-[var(--border)]">
        <td className="px-4 py-2 font-medium t-primary">{section.title}</td>
        <td className="px-4 py-2 text-right font-mono font-medium t-primary">
          {formatCents(section.subtotal)}
        </td>
        {showPrevious && (
          <td className="px-4 py-2 text-right font-mono t-muted">
            {section.previousSubtotal !== null ? formatCents(section.previousSubtotal) : '—'}
          </td>
        )}
      </tr>
      {section.accounts.map((a) => (
        <tr key={`${section.key}-${a.code}`} className="border-t border-[var(--border)]/60">
          <td className="px-4 py-1.5 pl-10 t-secondary">
            <span className="t-muted mr-2">{a.code}</span>
            {a.name}
          </td>
          <td className="px-4 py-1.5 text-right font-mono t-secondary">
            {formatCents(a.amount)}
          </td>
          {showPrevious && (
            <td className="px-4 py-1.5 text-right font-mono t-muted">
              {a.previousAmount !== null ? formatCents(a.previousAmount) : '—'}
            </td>
          )}
        </tr>
      ))}
    </>
  )
}
